import React from "react";
import { Box, Card, Flex, Text } from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { useTranslation } from "react-i18next";
import { OKNSegmentTrace, oknDirectionLabelKey } from "@utils/oknChartData";

const LEFT_EYE_COLOR = "#228be6";
const RIGHT_EYE_COLOR = "#fa5252";

interface OKNEyePositionTraceChartProps {
  trace: OKNSegmentTrace;
  height?: number;
}

/**
 * One panel of the OKN trace grid: horizontal (or vertical) eye position over
 * time for a single motion segment, showing the slow-phase / fast-phase
 * nystagmus sawtooth for both eyes.
 */
const OKNEyePositionTraceChart: React.FC<OKNEyePositionTraceChartProps> = ({
  trace,
  height = 220,
}) => {
  const { t } = useTranslation();

  const title = t("okn.tracePanelTitle", {
    direction: t(oknDirectionLabelKey(trace.direction)),
    speed: trace.speedDegPerSecond,
  });

  const hasData = trace.points.some((p) => p.left != null || p.right != null);

  return (
    <Card withBorder radius="md" p="sm">
      <Text fw={600} size="sm" ta="center" mb="xs">
        {title}
      </Text>
      <Box h={height}>
        {hasData ? (
          <LineChart
            h={height}
            data={trace.points}
            dataKey="t"
            series={[
              { name: "left", label: t("okn.horizontalLeft"), color: LEFT_EYE_COLOR },
              { name: "right", label: t("okn.horizontalRight"), color: RIGHT_EYE_COLOR },
            ]}
            curveType="linear"
            withDots={false}
            connectNulls={false}
            gridAxis="xy"
            withLegend
            legendProps={{ verticalAlign: "top", height: 28 }}
            xAxisLabel={t("okn.timeSeconds")}
            yAxisLabel={t("okn.eyePositionDeg")}
            xAxisProps={{ type: "number", domain: ["dataMin", "dataMax"] }}
            valueFormatter={(value) => (value != null ? `${Number(value).toFixed(1)}°` : "—")}
            referenceLines={[{ y: 0, color: "gray.5" }]}
          />
        ) : (
          <Flex justify="center" align="center" h="100%">
            <Text c="dimmed" size="sm">
              {t("okn.noTraceData")}
            </Text>
          </Flex>
        )}
      </Box>
    </Card>
  );
};

export default React.memo(OKNEyePositionTraceChart);
